import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { ToastNotificationService } from '../toast-notification/toast-notification.service';

@Component({
  selector: 'atom-copy-id',
  templateUrl: './copy-id.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inline-flex min-w-0 items-center',
    '[class.justify-end]': 'align() === "end"',
  },
})
export class CopyId implements OnDestroy {
  /** The ID (or other string) shown and copied to the clipboard. */
  readonly value = input.required<string>();
  readonly label = input('ID');
  readonly align = input<'start' | 'end'>('start');

  readonly copied = signal(false);

  readonly copyAriaLabel = computed(() =>
    this.copied() ? `Copied ${this.label()}` : `Copy ${this.label()}`,
  );

  readonly copyTitle = computed(() => `Copy ${this.label()}`);

  readonly idButtonClass = computed(() => (this.align() === 'end' ? 'text-right' : 'text-left'));

  private copiedResetTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly toastNotificationService = inject(ToastNotificationService);

  ngOnDestroy(): void {
    this.clearCopiedResetTimeout();
  }

  async copyId(): Promise<void> {
    const value = this.value()?.trim();
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      this.copied.set(true);
      this.clearCopiedResetTimeout();
      this.copiedResetTimeoutId = setTimeout(() => {
        this.copied.set(false);
        this.copiedResetTimeoutId = null;
      }, 2000);
      await this.toastNotificationService.alert({
        title: 'Copied',
        message: `${this.label()} copied to clipboard!`,
        type: 'success',
        icon: 'success',
      });
    } catch {
      await this.toastNotificationService.alert({
        title: 'Copy failed',
        message: `Could not copy ${this.label()} to clipboard.`,
        type: 'error',
        icon: 'error',
      });
    }
  }

  private clearCopiedResetTimeout(): void {
    if (this.copiedResetTimeoutId) {
      clearTimeout(this.copiedResetTimeoutId);
      this.copiedResetTimeoutId = null;
    }
  }
}
